import {
  AddTask,
  ArrowUpwardRounded,
  BorderClear,
  CheckRounded,
  Download,
  ExpandMoreRounded as ExpandMoreRoundedIcon
} from '@mui/icons-material'
import AppsIcon from '@mui/icons-material/Apps'
import ListIcon from '@mui/icons-material/List'
import { Box, ButtonGroup, Divider, ListItemIcon, Menu, Typography } from '@mui/material'
import { MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { setGlobalOrderBy, setGlobalRole } from '../../../../../redux/actions/global'
import { clearSelection, setShowCheckboxes } from '../../../../../redux/actions/multipleSelection'
import { getRoles } from '../../../../../redux/actions/roles'
import { IRootState } from '../../../../../redux/types'
import useScreenWidth from '../../../hooks/useScreenWidth'
import {
  ActionButton,
  ActionButtonNoLine,
  ContainerFilter,
  ContainerResp,
  FilterButton,
  FilterMenuItem
} from '../../../styles/ContentFilterStyles'
import ButtonBack from '../../buttons/ButtonBack'
import ContentSearch from '../../filters/ContentSearch'

type ContentFilterProps = {
  changeDisplay?: any
  sectionName?: any
  showFilterBy?: boolean
  showOrderBy?: boolean
  comercial?: boolean
  handleLive?: any
  goBackAction?: boolean
  handleContentSearch?: any
  handleAllResourcesDownload?: (resource_id: number[]) => void
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
function ContentFilter({
  changeDisplay = null,
  sectionName = null,
  showFilterBy = true,
  showOrderBy = true,
  handleContentSearch,
  goBackAction = false,
  comercial = false,
  handleAllResourcesDownload
}: ContentFilterProps) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const isLaptop = useScreenWidth(1280)
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(search)
  const dispatch = useDispatch()

  const orderByOptionsTranslate = [t('more-recent'), t('more-seen'), 'VIP', t('featured')]
  const [anchorLanRole, setAnchorLanRole] = useState<null | HTMLElement>(null)
  const [anchorLanOrderBy, setAnchorLanOrderBy] = useState<null | HTMLElement>(null)
  const [orderByOptions, setOrderByOptions] = useState<string[]>([])

  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { globalRoleId, globalOrderById } = useSelector((state: IRootState) => state.global)
  const { roles, rolehierarchy } = useSelector((state: IRootState) => state.role)
  const { showCheckboxes: showing, selectedCards } = useSelector((state: IRootState) => state.multipleSelection)
  const hasSelectedCards = selectedCards && selectedCards.length > 0
  const [display, setDisplay] = useState<number>(1)

  const selectedRole = roles.find((role: any) => role.id === globalRoleId)

  useEffect(() => {
    if (roles.length === 0) dispatch(getRoles())
    if (globalRoleId === null) dispatch(setGlobalRole(null))
    if (search && searchParams.get('globalOrderById') !== null) {
      handleChangeOrderBy(Number(searchParams.get('globalOrderById')))
    }
  }, [])

  useEffect(() => {
    if (roles.length > 0) {
      if (search && searchParams.get('globalRoleId') !== null && authUser.role_id !== 6) {
        handleChangeRole(Number(searchParams.get('globalRoleId')))
      } else {
        searchParams.delete('globalRoleId')
        navigate(`${pathname}?${searchParams}`, { replace: true })
      }
    }
  }, [roles])

  useEffect(() => {
    if (pathname.includes('cursos')) {
      let tmporderByOptions = [...orderByOptionsTranslate]
      tmporderByOptions.push(t('client.courses.viewed-courses'))
      tmporderByOptions.push(t('client.courses.recommended'))
      setOrderByOptions(tmporderByOptions)
      handleChangeOrderBy(5)
    } else if (pathname.includes('biblioteca')) {
      setOrderByOptions(orderByOptionsTranslate)
      handleChangeOrderBy(0)
    } else if (pathname.includes('comercial')) {
      setOrderByOptions(orderByOptionsTranslate)
      handleChangeOrderBy(0)
    } else {
      setOrderByOptions(orderByOptionsTranslate)
    }
  }, [t])

  /* Set global variables */
  const handleChangeRole = (role_id: null | number) => {
    dispatch(setGlobalRole(role_id))
    if (role_id != null) searchParams.set('globalRoleId', role_id.toString())
    else searchParams.delete('globalRoleId')
    navigate(`${pathname}?${searchParams}`, { replace: true })
  }

  const handleChangeOrderBy = (orderById: number) => {
    localStorage.setItem('globalOrderById', orderById.toString())
    dispatch(setGlobalOrderBy(orderById))
    searchParams.set('globalOrderById', orderById.toString())
    navigate(`${pathname}?${searchParams}`, { replace: true })
  }

  // Set local variables html events
  const handleClickLanRole = (event: MouseEvent<HTMLElement>) => {
    setAnchorLanRole(event.currentTarget)
  }

  const handleClickLanOrderBy = (event: MouseEvent<HTMLElement>) => {
    setAnchorLanOrderBy(event.currentTarget)
  }

  const handleCloseLanRole = () => {
    setAnchorLanRole(null)
  }

  const handleCloseLanOrderBy = () => {
    setAnchorLanOrderBy(null)
  }

  const handleShowCheckboxes = () => {
    dispatch(setShowCheckboxes(!showing))
  }

  const handleClearSelection = () => {
    dispatch(clearSelection())
  }

  // Display Mode for Library
  const changeDisplayMode = (mode: number) => {
    setDisplay(mode)
    changeDisplay(mode)
  }

  return (
    <ContainerFilter className={comercial ? 'noPadding' : ''}>
      {sectionName === null ? (
        <>{goBackAction !== false && <ButtonBack customText={t('back-mobile')} />}</>
      ) : (
        <ContentSearch sectionName={sectionName} handleContentSearch={handleContentSearch} />
      )}
      <ContainerResp display="flex" flexWrap="wrap" ml={'auto'} justifyContent="flex-end">
        {pathname.includes('biblioteca') && (
          <Box display="flex" alignItems="center" mr="auto">
            {!hasSelectedCards && (
              <>
                {!isLaptop && (
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {t('multiple')}
                  </Typography>
                )}
                <ActionButton onClick={handleShowCheckboxes} endIcon={<AddTask />}>
                  {isLaptop ? t('select') : t('selection')}
                </ActionButton>
              </>
            )}
            {hasSelectedCards && (
              <>
                <ActionButton onClick={handleClearSelection} endIcon={<BorderClear />}>
                  {t('clear-all')}
                </ActionButton>
                <ActionButton onClick={() => handleAllResourcesDownload?.(selectedCards ?? [])} endIcon={<Download />}>
                  {t('download')}
                </ActionButton>
              </>
            )}
          </Box>
        )}
        {showFilterBy && (
          <Box
            display="flex"
            alignItems="center"
            //mr={isLaptop ? 2 : 0}
          >
            {authUser.role_id !== 6 && (
              <>
                {!isLaptop && (
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {t('filter-by-role')}
                  </Typography>
                )}
                <FilterButton
                  aria-controls="role-filter"
                  aria-label="role-filter"
                  aria-haspopup="true"
                  onClick={handleClickLanRole}
                  endIcon={<ExpandMoreRoundedIcon htmlColor=" #FC3E6F" />}
                >
                  {isLaptop ? (
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {t('role')}
                    </Typography>
                  ) : (
                    <>{globalRoleId === null ? t('all') : selectedRole ? selectedRole.name : t('all')}</>
                    // <>{globalRoleId === null ? t('all') : roles.find((role: any) => role.id === globalRoleId).name}</>
                  )}
                </FilterButton>

                <Menu
                  id="menu-role-filter"
                  anchorEl={anchorLanRole}
                  keepMounted
                  open={Boolean(anchorLanRole)}
                  onClose={handleCloseLanRole}
                  onClick={handleCloseLanRole}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                  <FilterMenuItem onClick={() => handleChangeRole(null)}>
                    {t('all')}
                    {globalRoleId === null && (
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <CheckRounded htmlColor="#8DDD55" />
                      </ListItemIcon>
                    )}
                  </FilterMenuItem>
                  {roles.map((role: any) => {
                    if ((rolehierarchy.includes(role.id) || role.id === authUser.role_id) && ![1, 2, 5, 7].includes(role.id)) {
                      return (
                        <FilterMenuItem key={role.id} onClick={() => handleChangeRole(role.id)}>
                          {role.name}
                          {globalRoleId === role.id && (
                            <ListItemIcon style={{ minWidth: 32 }}>
                              <CheckRounded htmlColor="#8DDD55" />
                            </ListItemIcon>
                          )}
                        </FilterMenuItem>
                      )
                    }
                  })}
                </Menu>
              </>
            )}
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={isLaptop ? 2 : 3} flexWrap="nowrap">
          {showOrderBy && (
            <Box display="flex" alignItems="center">
              {!isLaptop && (
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {t('order-by')}
                </Typography>
              )}
              <FilterButton
                aria-controls="orderby-filter"
                aria-label="orderby-filter"
                aria-haspopup="true"
                onClick={handleClickLanOrderBy}
                endIcon={<ArrowUpwardRounded color="primary" />}
              >
                {isLaptop ? (
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {t('sort')}
                  </Typography>
                ) : (
                  <>{orderByOptions[globalOrderById]}</>
                )}
              </FilterButton>

              <Menu
                id="menu-orderby-filter"
                anchorEl={anchorLanOrderBy}
                keepMounted
                open={Boolean(anchorLanOrderBy)}
                onClose={handleCloseLanOrderBy}
                onClick={handleCloseLanOrderBy}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                {orderByOptions.map((orderBy: string, index: number) => (
                  <FilterMenuItem key={orderBy} onClick={() => handleChangeOrderBy(index)}>
                    {orderBy}
                    {globalOrderById === index && (
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <CheckRounded htmlColor="#8DDD55" />
                      </ListItemIcon>
                    )}
                  </FilterMenuItem>
                ))}
              </Menu>
            </Box>
          )}
          {pathname.includes('biblioteca') && (
            <Box display="flex" alignItems="center" pl={isMobile ? 0 : 1}>
              {!isMobile && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ width: 'auto', height: '30px', margin: 'auto', backgroundColor: 'primary.main' }}
                />
              )}
              {isMobile ? (
                display ? (
                  <ActionButton
                    onClick={() => changeDisplayMode(0)}
                    sx={[display ? { color: 'primary.main' } : {}]}
                    startIcon={<ListIcon />}
                  />
                ) : (
                  <ActionButtonNoLine
                    onClick={() => changeDisplayMode(1)}
                    sx={[display ? {} : { color: 'primary.main' }]}
                    startIcon={<AppsIcon />}
                  />
                )
              ) : (
                <ButtonGroup
                  disableElevation
                  variant="text"
                  sx={{
                    '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                      borderRight: 'none'
                    }
                  }}
                >
                  <ActionButtonNoLine
                    onClick={() => changeDisplayMode(0)}
                    sx={display === 1 ? { color: 'black' } : { color: 'primary.main' }}
                    startIcon={<ListIcon />}
                  />
                  <ActionButton
                    onClick={() => changeDisplayMode(1)}
                    sx={display === 0 ? { color: 'black' } : { color: 'primary.main' }}
                    startIcon={<AppsIcon />}
                  />
                </ButtonGroup>
              )}
            </Box>
          )}
        </Box>
      </ContainerResp>
    </ContainerFilter>
  )
}

export default ContentFilter
